import {
    ApolloClient,
    InMemoryCache,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@smithy/protocol-http';
import { SignatureV4 } from '@smithy/signature-v4';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import log from 'loglevel';

import { standardizeUser } from './UserContext';

import type { PaginatedAwards } from '../types/types';

const cognitoUri = import.meta.env.VITE_GRAPHQL_COGNITO_URI || 'https://jm4jmmxdf1.execute-api.us-east-1.amazonaws.com';
const iamUri = import.meta.env.VITE_GRAPHQL_IAM_URI || 'https://lsthmqsr29.execute-api.us-east-1.amazonaws.com/LaudGraphQLServer';

async function awsGraphqlFetch(_uri: string, options: RequestInit) {
    let user: { type: string } | Record<string, unknown> = { type: 'unauthenticated' };
    let session: Awaited<ReturnType<typeof fetchAuthSession>> | undefined;
    try {
        await getCurrentUser();
        session = await fetchAuthSession();
        if (session.tokens?.idToken) {
            user = { type: 'Cognito' };
        }
    } catch (err) {
        log.error(err);
    }
    const standardUser = standardizeUser(user as { type: string });
    if (standardUser.expires_at && standardUser.expires_at < Date.now()) {
        log.info('Credentials expired; logging user out');
        await signOut({ global: true });
        const search = `?nextPage=${window.location.pathname}${window.location.search}`;
        window.location.assign(`/signIn${search}`);
    } else if (standardUser.type === 'Cognito') {
        const idJwt = session!.tokens?.idToken?.toString();
        (options.headers as Record<string, string>).Authorization = idJwt!;
    } else if (standardUser.type === 'unauthenticated') {
        log.info('Unauthenticated user');
        session = await fetchAuthSession();
        const credentials = session.credentials!;

        const url = new URL(iamUri);
        const request = new HttpRequest({
            method: options.method || 'POST',
            hostname: url.hostname,
            path: url.pathname,
            headers: {
                'Content-Type': 'application/json',
                host: url.hostname,
            },
            body: options.body as string,
        });

        const signer = new SignatureV4({
            credentials: {
                accessKeyId: credentials.accessKeyId,
                secretAccessKey: credentials.secretAccessKey,
                sessionToken: credentials.sessionToken,
            },
            region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
            service: 'execute-api',
            sha256: Sha256,
        });

        const signedRequest = await signer.sign(request);
        return fetch(iamUri, {
            ...options,
            headers: signedRequest.headers,
        });
    } else {
        log.error(`Unknown user type: ${standardUser.type}`);
    }
    return fetch(cognitoUri, options);
}

const apolloClient = new ApolloClient({
    link: new BatchHttpLink({
        uri: cognitoUri,
        fetch: awsGraphqlFetch as typeof fetch,
    }),
    cache: new InMemoryCache({
        typePolicies: {
            Award: { keyFields: ['AwardId'] },
            Organization: { keyFields: ['OrganizationId'] },
            Template: { keyFields: ['TemplateId'] },
            User: { keyFields: ['UserName'] },
            LogoImage: { keyFields: ['LogoImageId'] },
            Grantor: { keyFields: ['UserName', 'OrganizationId'] },
            TeamMember: { keyFields: ['ManagerUserName', 'Email', 'OrganizationId'] },
            Event: { keyFields: ['EventId'] },
            GrantRequest: { keyFields: ['RequestId'] },
            Query: {
                fields: {
                    getAwardsForOrganization: {
                        keyArgs: false,
                        merge(existing: PaginatedAwards | undefined, incoming: PaginatedAwards | undefined) {
                            const merged: PaginatedAwards = {
                                Awards: [],
                                Paginator: { LastEvaluatedKey: '' },
                            };
                            if (existing?.Awards) {
                                merged.Awards = existing.Awards.slice(0);
                                merged.Paginator = { ...existing.Paginator };
                            }
                            if (incoming) {
                                merged.Awards = merged.Awards.concat(incoming.Awards);
                                merged.Paginator = { ...incoming.Paginator };
                            }
                            return merged;
                        },
                    },
                },
            },
        },
    }),
    devtools: { enabled: true },
    defaultOptions: {
        watchQuery: {
            notifyOnNetworkStatusChange: false,
        },
    },
});

export default apolloClient;
